import { Trash2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { CadenceSelect } from '@/components/CadenceSelect'
import { ModeSelect } from '@/components/ModeSelect'
import { RunForm } from '@/components/RunForm'
import type { RunFormValues } from '@/components/RunForm.types'
import { SeedInputReveal } from '@/components/SeedInputReveal'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { cadenceLabel } from '@/lib/cadence'
import { modeLabel } from '@/lib/format'
import {
  useCreateSchedule,
  useDeleteSchedule,
  useSchedules,
} from '@/lib/queries'
import { mergeSeedDraft } from '@/lib/seeds'
import type { Cadence, Mode } from '@/lib/types'
import { useAnalytics } from '@/provider/useAuth'

// Reachable only when auth is present (routed under TrackerApp's authed branch).
// Lists the user's schedules and lets them create/delete them. The
// create + delete mutations invalidate the ['schedules'] query so the list
// stays in sync without a manual refetch.
export default function Schedules() {
  const { data, isPending, isError } = useSchedules()
  const create = useCreateSchedule()
  const del = useDeleteSchedule()
  const analytics = useAnalytics()
  // Bumped after a successful create to clear the RunForm's brand/category.
  const [resetSignal, setResetSignal] = useState(0)
  const [mode, setMode] = useState<Mode>('direct')
  const [cadence, setCadence] = useState<Cadence>('biweekly')
  const [clusters, setClusters] = useState<string[]>([])
  const [clusterDraft, setClusterDraft] = useState('')

  async function createSchedule(values: RunFormValues) {
    // Fold in any uncommitted draft so typed-but-not-entered text is not dropped.
    const finalClusters =
      mode === 'intent_map' ? mergeSeedDraft(clusters, clusterDraft) : undefined
    const { targetMarketLabel, ...scheduleInput } = values

    try {
      await create.mutateAsync({
        ...scheduleInput,
        mode,
        cadence,
        clusters: finalClusters,
      })
      analytics.track('schedule_created', {
        brand: values.brand,
        category: values.category,
        targetMarketIds: values.targetMarketIds,
        targetMarketLabel,
        mode,
        cadence,
      })
      setResetSignal((n) => n + 1)
      setClusters([])
      setClusterDraft('')
      toast.success(`${cadenceLabel(cadence)} schedule created.`)
    } catch (err) {
      // ApiError carries the server's quota/auth message; a bare Error has none.
      toast.error(
        err instanceof Error ? err.message : 'Could not create schedule.'
      )
    }
  }

  function deleteSchedule(id: string, scheduleBrand: string) {
    del.mutate(id, {
      onSuccess: () => {
        analytics.track('schedule_deleted', { scheduleId: id })
        toast.success(`Schedule for ${scheduleBrand} deleted.`)
      },
      onError: (err) =>
        toast.error(
          err instanceof Error ? err.message : 'Could not delete schedule.'
        ),
    })
  }

  return (
    <main className="px-6 pb-20 mx-auto w-full max-w-4xl">
      <section className="pt-8 pb-4">
        <h1 className="text-3xl font-semibold">Schedules</h1>
        <p className="mt-1 text-muted-foreground">
          Re-run an analysis automatically on a repeating schedule.
        </p>
      </section>

      {/* Shares the Landing run form via <RunForm>; the cadence control, button
          copy, and card wrapper are the route-specific bits. */}
      <RunForm
        idPrefix="schedule"
        className="rounded-xl border border-border bg-card p-5 space-y-4"
        pending={create.isPending}
        submitLabel="Create schedule"
        pendingLabel="Creating schedule…"
        helperText="Runs automatically on the chosen cadence across the active AI models."
        onSubmit={(values) => void createSchedule(values)}
        resetSignal={resetSignal}
        header={
          <h2 className="text-xs uppercase tracking-wider text-muted-foreground">
            New schedule
          </h2>
        }
        modeControl={
          <ModeSelect name="schedule-mode" value={mode} onChange={setMode} />
        }
        runDetail={
          // Tailwind drops this gap when the seed reveal is closed, since the
          // Cadence fieldset is then the only child.
          <div className="space-y-6">
            <SeedInputReveal
              show={mode === 'intent_map'}
              id="schedule-clusters"
              values={clusters}
              draft={clusterDraft}
              disabled={create.isPending}
              onValuesChange={setClusters}
              onDraftChange={setClusterDraft}
            />

            <CadenceSelect
              name="schedule-cadence"
              value={cadence}
              disabled={create.isPending}
              onChange={setCadence}
            />
          </div>
        }
      />

      <section className="pt-8">
        {isPending && (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              // Static placeholder list; index keys are fine here.
              // biome-ignore lint/suspicious/noArrayIndexKey: fixed skeleton rows
              <Skeleton key={i} className="h-16 w-full rounded-xl" />
            ))}
          </div>
        )}
        {isError && (
          <p className="py-8 text-center text-destructive">
            Could not load schedules.
          </p>
        )}
        {data && data.length === 0 && (
          <p className="py-8 text-center text-muted-foreground">
            No schedules yet.
          </p>
        )}
        {data && data.length > 0 && (
          <ul className="space-y-2">
            {data.map((s) => (
              <li
                key={s.id}
                className="flex items-center justify-between gap-4 rounded-xl border border-border bg-card px-4 py-3"
              >
                <div className="min-w-0">
                  <div className="flex min-w-0 items-center gap-2 font-medium">
                    {s.mode === 'intent_map' ? (
                      <span className="shrink-0 rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-primary">
                        {modeLabel(s.mode)}
                      </span>
                    ) : (
                      <span className="shrink-0 rounded-full border border-border bg-muted px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                        Normal
                      </span>
                    )}
                    <span className="min-w-0 truncate">
                      {s.brand}{' '}
                      <span className="text-muted-foreground font-normal">
                        in
                      </span>{' '}
                      {s.category}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {s.targetMarketLabel ?? s.market ?? 'Global'} ·{' '}
                    {cadenceLabel(s.cadence)} · next{' '}
                    {new Date(s.nextRunAt).toLocaleDateString()} ·{' '}
                    {s.enabled ? 'on' : 'off'}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="shrink-0 text-muted-foreground hover:text-destructive"
                  onClick={() => deleteSchedule(s.id, s.brand)}
                  disabled={del.isPending}
                  aria-label={`Delete schedule for ${s.brand}`}
                >
                  <Trash2 className="size-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </main>
  )
}
